import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {apiClient} from '@common/http/query-client';
import {useAuth} from '@common/auth/use-auth';
import {useSelectedLocale} from '@ui/i18n/selected-locale';

export type ListingType =
  | 'scholarship' | 'university' | 'course' | 'job' | 'program';

export interface FavoriteRow {
  id: number;
  listing_type: ListingType;
  listing_id: number;
  listing_slug: string | null;
  listing_title: string | null;
  listing_logo: string | null;
  listing_country: string | null;
  created_at: string;
}

/** Snapshot passed when toggling, so the Favorites page can render a card. */
export interface FavoriteTarget {
  listing_type: ListingType;
  listing_id: number;
  listing_slug?: string | null;
  listing_title?: string | null;
  listing_logo?: string | null;
  listing_country?: string | null;
}

export const favKey = (type: string, id: number | string) => `${type}:${id}`;

const IDS_KEY = ['favorites', 'ids'];
const LIST_KEY = ['favorites', 'list'];

/**
 * The set of "type:id" keys the current user has favorited. Drives every heart
 * icon's filled/outlined state. Returns an empty set for guests (the endpoint
 * itself returns [] rather than 401), so hearts render as "not favorited".
 */
export function useFavoriteIds() {
  const {isLoggedIn} = useAuth();
  return useQuery({
    queryKey: IDS_KEY,
    queryFn: () =>
      apiClient
        .get('opp/favorites/ids')
        .then(r => new Set<string>(r.data.ids as string[])),
    // Guests still get an (empty) set so components don't special-case null.
    enabled: true,
    staleTime: 60 * 1000,
    // Don't spam the endpoint for logged-out users after the first empty read.
    gcTime: isLoggedIn ? 5 * 60 * 1000 : Infinity,
  });
}

/**
 * Toggle a listing in/out of favorites with an optimistic cache update, so the
 * heart flips instantly. Rolls back on error and refreshes the Favorites list.
 */
export function useToggleFavorite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (target: FavoriteTarget) =>
      apiClient.post('opp/favorites/toggle', target).then(r => r.data),
    onMutate: async (target: FavoriteTarget) => {
      await qc.cancelQueries({queryKey: IDS_KEY});
      const prev = qc.getQueryData<Set<string>>(IDS_KEY);
      const next = new Set(prev ?? []);
      const key = favKey(target.listing_type, target.listing_id);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      qc.setQueryData(IDS_KEY, next);
      return {prev};
    },
    onError: (_e, _target, ctx) => {
      if (ctx?.prev) qc.setQueryData(IDS_KEY, ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({queryKey: IDS_KEY});
      qc.invalidateQueries({queryKey: LIST_KEY});
    },
  });
}

/** The current user's favorites, for the Favorites page. */
export function useFavorites() {
  const {isLoggedIn} = useAuth();
  // Locale in the key + sent as `lang` so titles re-localize on language switch
  // (the server refreshes each title from the live listing per the locale).
  const {localeCode} = useSelectedLocale();
  return useQuery({
    queryKey: [...LIST_KEY, localeCode],
    queryFn: () =>
      apiClient
        .get('opp/favorites', {params: {lang: localeCode}})
        .then(r => r.data.favorites as FavoriteRow[]),
    enabled: isLoggedIn,
  });
}
